import { PLAYER_H, PLAYER_W, WORLD_H, WORLD_W, type HazardDef, type LevelDef, type Platform, type PlayerState, type Rect, type WorldState } from '../../shared/types';
import { isHazardActive } from '../../shared/simulation';
import { clamp, mix } from './paint';

export const CAMERA_FOOTER = 40;
export const MIN_PLAY_SCALE = 0.6; // Only exceptionally tall teams in a short viewport approach this floor.
export const DESKTOP_PLAY_SCALE = 1.72;
/** Phones in portrait keep a 32-px body at least 36 CSS px wide. */
export const PORTRAIT_PLAY_SCALE = 1.125;
/** A deadly surface this close (world px, horizontal) is framed together with the courier. */
export const DANGER_RANGE = 160;
const DANGER_RELEASE = 210;
const DANGER_HOLD_SECONDS = 0.8;
/** World px of the hazard below its surface line that the biased frame keeps visible. */
export const DANGER_SURFACE = 22;
const DANGER_EDGE_INSIDE = 56;
const DANGER_EDGE_OUTSIDE = 26;
/** Default Team-view framing: the local courier's floor line sits this far down the frame (sky above,
 * ground and whatever lies below it in the lower band). Portrait phones keep a little more room above. */
export const FLOOR_LINE = 0.72;
export const FLOOR_LINE_PORTRAIT = 0.70;
/** The local courier is never framed above this fraction of the frame height. */
export const CEILING_FRACTION = 0.15;
/** World px an ordinary jump rises (PHYSICS.jump² / 2·gravity ≈ 92): reserved above the resting body. */
const JUMP_APEX = 92;
/** World px kept between a jump apex (of the whole support chain at rest, so a stack jump too) and the frame's top. */
const JUMP_HEADROOM = 16;
/** The follow frame's floor line eases to a new support (a landing on a higher/lower surface) at this rate
 * (1/s): 63 % after 100 ms, ~95 % after 300 ms. */
const FLOOR_EASE_RATE = 10;
/** A support line this close (world px) to the eased one is the same support: the frame holds its Y while airborne. */
const SAME_SUPPORT = 0.5;
/** Far-bank margin kept beyond an uncovered water/bramble span when the frame leans toward it. */
const FAR_BANK = 26;
/** The lean toward a far bank never moves the frame more than this fraction of the visible width off
 * the default framing, so the courier's own surroundings (its plate, its sign) stay in frame. */
const FAR_BANK_LEAN = 0.1;

export interface CameraView {
  x: number;
  y: number;
  scale: number;
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
  follow: boolean;
  compact: boolean;
  teamIds?: readonly string[];
  teamBounds?: Readonly<Rect>;
  /** Deadly surfaces currently biasing the follow framing (see dangerZones). */
  dangers?: readonly DangerZone[];
  /** The frame was held this sample because the focus body is at rest (see TrailCamera.sample). */
  held?: boolean;
  /** The frame kept its vertical position this sample because the focus body is airborne over the same support. */
  heldY?: boolean;
  /** The eased support line the vertical framing follows, and the raw support line it eases toward (world y). */
  floorLine?: number;
  supportLine?: number;
}

export interface DangerZone {
  id: string;
  hazardId: string;
  kind: 'water' | 'bramble';
  /** The uncovered horizontal span a courier at this height could fall into. */
  span: readonly [number, number];
  /** World y of the deadly surface. */
  surface: number;
  /** The world rectangle the biased frame tries to keep visible: the near edge plus surface. */
  frame: Rect;
  /** Horizontal world distance from the courier body to the span (0 while above it). */
  distance: number;
  /** Where the span lies relative to the courier. */
  side: 'left' | 'right' | 'over';
}

function subtract(spans: [number, number][], from: number, to: number): [number, number][] {
  return spans.flatMap(([a, b]) => {
    if (to <= a || from >= b) return [[a, b] as [number, number]];
    const kept: [number, number][] = [];
    if (from > a) kept.push([a, from]);
    if (to < b) kept.push([to, b]);
    return kept;
  });
}

/**
 * The deadly water/bramble surfaces a courier could actually fall into from its
 * current height: each active hazard's horizontal extent minus every fixed platform that
 * currently exists between the courier's feet and that surface (a latched bridge covers
 * the gap; a moving ferry does not, since it leaves). Pure view geometry over received
 * state; it never changes a game outcome.
 */
export function dangerZones(level: LevelDef, platforms: readonly Platform[], world: WorldState | null, body: Rect, range: number): DangerZone[] {
  const feet = body.y + body.h;
  const centre = body.x + body.w / 2;
  const zones: DangerZone[] = [];
  const active = (hazard: HazardDef) => world ? isHazardActive(hazard, world) : !hazard.disabledBy?.length;
  for (const hazard of level.hazards) {
    if ((hazard.kind !== 'water' && hazard.kind !== 'bramble') || !active(hazard)) continue;
    if (feet > hazard.y + hazard.h) continue;
    let spans: [number, number][] = [[hazard.x, hazard.x + hazard.w]];
    for (const platform of platforms) {
      // A moving deck leaves; only a platform that stays covers the surface beneath it.
      if (platform.motion || platform.y < feet - 1 || platform.y >= hazard.y || platform.w <= 0) continue;
      spans = subtract(spans, platform.x, platform.x + platform.w);
    }
    spans.forEach(([a, b], index) => {
      // A body is 32 wide: slivers narrower than that cannot be fallen into.
      if (b - a < 30) return;
      const distance = Math.max(0, a - (body.x + body.w), body.x - b);
      if (distance > range) return;
      const side = centre < a ? 'right' : centre > b ? 'left' : 'over';
      const x0 = side === 'right' ? a - DANGER_EDGE_OUTSIDE : side === 'left' ? Math.max(a, b - DANGER_EDGE_INSIDE) : Math.max(a, centre - 120);
      const x1 = side === 'right' ? Math.min(b, a + DANGER_EDGE_INSIDE) : side === 'left' ? b + DANGER_EDGE_OUTSIDE : Math.min(b, centre + 120);
      zones.push({ id: `${hazard.id}:${index}`, hazardId: hazard.id, kind: hazard.kind as 'water' | 'bramble', span: [a, b], surface: hazard.y,
        frame: { x: x0, y: hazard.y, w: Math.max(1, x1 - x0), h: DANGER_SURFACE }, distance, side });
    });
  }
  return zones.sort((a, b) => a.distance - b.distance);
}

export function toScreen(view: CameraView, point: { x: number; y: number }): { x: number; y: number } {
  return { x: view.offsetX + (point.x - view.x) * view.scale, y: view.offsetY + (point.y - view.y) * view.scale };
}

export function screenRect(view: CameraView, rect: Rect): Rect {
  return { ...toScreen(view, rect), w: rect.w * view.scale, h: rect.h * view.scale };
}

/** A view of received game state; it never moves a body or changes a game outcome. */
export class TrailCamera {
  private ready = false;
  private time = 0;
  private zoomInAt = 0;
  private memories = new Map<string, { restY: number; nearUntil: number; /** World y of the ground under the body's LAST support chain (its root's feet); kept through a jump or a fall. */ floorY: number }>();
  private dangerHold = new Map<string, number>();
  private view: CameraView = { x: 0, y: 0, scale: 1, offsetX: 0, offsetY: 0, width: WORLD_W, height: WORLD_H, follow: false, compact: false };
  /** The focus body last frame (and whether it was still); the frame is held while it stays that body, at rest. */
  private rest: { id: string; x: number; y: number; still: boolean } | null = null;
  /** The focus courier's SUPPORT line (world y of the ground under its support chain), eased over ~300 ms when
   * the courier lands on another surface. The vertical framing follows this line, never the airborne body. */
  private floor: { id: string; line: number; target: number } | null = null;
  /** Diagnostics: whether the last sample held the frame instead of easing toward the moving target. */
  held = false;
  /** Diagnostics: the last sample kept its vertical position because the focus body was airborne over the same support. */
  heldY = false;

  reset(): void { this.ready = false; this.memories.clear(); this.dangerHold.clear(); this.zoomInAt = 0; this.rest = null; this.floor = null; this.held = false; this.heldY = false; }

  sample(
    width: number, canvasHeight: number, players: PlayerState[], localId: string | null,
    level: LevelDef | null, requested: 'follow' | 'overview' | undefined, dt: number, reducedMotion: boolean,
    scene: { world: WorldState; platforms: readonly Platform[] } | null = null,
    /** A world rectangle to keep in the follow frame while it can be (the fall location of a team reset). */
    keep: Rect | null = null,
  ): CameraView {
    const height = Math.max(100, canvasHeight - CAMERA_FOOTER);
    this.time += dt;
    const fit = Math.min(width / WORLD_W, height / WORLD_H);
    const compact = fit < 0.76;
    const follow = requested !== 'overview';
    if (!follow) {
      this.view = { x: 0, y: 0, scale: fit, offsetX: (width - WORLD_W * fit) / 2, offsetY: (height - WORLD_H * fit) / 2, width, height, follow, compact };
      this.ready = false;
      this.rest = null;
      this.floor = null;
      this.held = false;
      this.heldY = false;
      return this.view;
    }
    const alive = players.filter(player => player.connected && !player.entered);
    const byId = new Map(alive.map(player => [player.id, player]));
    const local = alive.find(player => player.id === localId);
    const focusBody: PlayerState | undefined = local ?? (level ? [...alive].sort((a, b) => Math.abs(a.x - level.exit.x) - Math.abs(b.x - level.exit.x))[0] : alive[0]);
    const focus = focusBody
      ?? { x: (level?.exit.x ?? 64) + (level?.exit.w ?? 32) / 2 - PLAYER_W / 2, y: (level?.exit.y ?? 515) + (level?.exit.h ?? 40) - PLAYER_H };
    const preferred = width >= 900 && height >= 440 ? DESKTOP_PLAY_SCALE : width < 500 ? PORTRAIT_PLAY_SCALE : 1.22;
    const radius = clamp(width / preferred * 0.38, 120, 280);
    const engagedFields = level?.hazards.filter(hazard => hazard.kind === 'beam' && hazard.disabledBy?.length
      && focus.x + PLAYER_W >= hazard.x - 90 && focus.x <= hazard.x + hazard.w + 45) ?? [];
    // Deadly surfaces near the LOCAL courier bias the frame downward/outward. Hysteresis
    // (engage at 160, release beyond 210 after a short hold) keeps the shift from flickering.
    const dangers: DangerZone[] = [];
    if (local && level && scene) {
      for (const zone of dangerZones(level, scene.platforms, scene.world, { x: local.x, y: local.y, w: PLAYER_W, h: PLAYER_H }, DANGER_RELEASE)) {
        const held = (this.dangerHold.get(zone.hazardId) ?? -Infinity) >= this.time;
        if (zone.distance > DANGER_RANGE && !held) continue;
        if (zone.distance <= DANGER_RANGE) this.dangerHold.set(zone.hazardId, this.time + DANGER_HOLD_SECONDS);
        dangers.push(zone);
      }
    }
    const linked = new Set<string>(local ? [local.id] : []);
    for (let pass = 0; pass < alive.length; pass++) {
      for (const player of alive) {
        if (linked.has(player.id) && player.supportId && byId.has(player.supportId)) linked.add(player.supportId);
        if (player.supportId && linked.has(player.supportId)) linked.add(player.id);
      }
    }
    const roots = new Map<string, PlayerState>();
    for (const player of alive) {
      let root = player;
      const visited = new Set<string>();
      while (root.supportId && byId.has(root.supportId) && !visited.has(root.id)) { visited.add(root.id); root = byId.get(root.supportId)!; }
      roots.set(player.id, root);
    }
    // Weight sharing is an engagement, even when the helpers stand side by side.
    for (const plate of level?.switches ?? []) {
      const helpers = alive.filter(player => {
        const root = roots.get(player.id)!;
        return player.grounded && root.grounded && Math.abs(root.y + PLAYER_H - plate.y - plate.h) < 2 && root.x < plate.x + plate.w && root.x + PLAYER_W > plate.x;
      });
      const protectsFocus = engagedFields.some(hazard => hazard.disabledBy?.includes(plate.id));
      if (protectsFocus || helpers.some(player => linked.has(player.id))) for (const player of helpers) linked.add(player.id);
    }
    for (const player of alive) {
      const anchor = roots.get(player.id)!;
      const memory = this.memories.get(player.id) ?? { restY: player.y, nearUntil: 0, floorY: anchor.y + PLAYER_H };
      // A carrying base in flight is not a new resting height for its riders.
      // Remember the settled shape and reserve an ordinary jump ABOVE that shape.
      if (player.grounded && anchor.grounded && Math.abs(anchor.vy) < 1) { memory.restY = player.y; memory.floorY = anchor.y + PLAYER_H; }
      if (linked.has(player.id) || (Math.abs(player.x - focus.x) < radius && Math.abs(player.y - focus.y) < 270)) memory.nearUntil = this.time + 1.4;
      this.memories.set(player.id, memory);
    }
    const team = alive.filter(player => linked.has(player.id) || ((this.memories.get(player.id)?.nearUntil ?? 0) >= this.time && Math.abs(player.x - focus.x) < radius * 1.8));
    const members = team.length ? team : [focus];
    const left = Math.min(...members.map(player => player.x));
    const right = Math.max(...members.map(player => player.x + PLAYER_W));
    const top = Math.min(...members.map(player => player.y));
    const bottom = Math.max(...members.map(player => player.y + PLAYER_H));
    const restTop = team.length ? Math.min(...team.map(player => this.memories.get(player.id)!.restY)) : focus.y;
    const restBottom = team.length ? Math.max(...team.map(player => this.memories.get(player.id)!.restY + PLAYER_H)) : focus.y + PLAYER_H;
    // The canopy is part of the helping action. Keep its roof and the plate's floor
    // in short screens, reserving jump room above bodies rather than above the roof.
    const frameTop = Math.min(top, ...engagedFields.map(hazard => hazard.y - 8));
    const frameBottom = Math.max(bottom, ...engagedFields.map(hazard => hazard.y + hazard.h));
    const restCeiling = Math.min(restTop - 104, ...engagedFields.map(hazard => hazard.y - 8));
    const restFloor = Math.max(restBottom, ...engagedFields.map(hazard => hazard.y + hazard.h));
    // Portrait phones keep the 36-px body when partners spread out sideways; actual
    // containment below still zooms out for a group that cannot fit at that scale.
    const fittedAcross = (width - 48) / Math.max(150, right - left + 40);
    const fitted = Math.min(preferred, width < 500 ? Math.max(fittedAcross, PORTRAIT_PLAY_SCALE) : fittedAcross,
      (height - (engagedFields.length ? 70 : 32)) / Math.max(150, restFloor - restCeiling));
    const requestedScale = Math.max(MIN_PLAY_SCALE, fitted);
    const resized = this.view.width !== width || this.view.height !== height;
    const smoothing = !this.ready || resized || reducedMotion ? 1 : 1 - Math.exp(-dt * 11);
    let scale = requestedScale;
    if (this.ready && !resized && !reducedMotion) {
      if (requestedScale < this.view.scale - 0.004) {
        this.zoomInAt = this.time + 1.4;
        scale = Math.max(requestedScale, this.view.scale - dt * 0.42);
      } else if (requestedScale > this.view.scale + 0.004) {
        scale = this.time >= this.zoomInAt ? Math.min(requestedScale, this.view.scale + dt * 0.18) : this.view.scale;
      } else scale = this.view.scale;
    }
    // Containment is a view constraint, never a body correction. Very tall short-screen
    // stacks use the available height; ordinary 2–5-body groups retain the close scale.
    scale = Math.min(scale, (width - 8) / Math.max(1, right - left), (height - 8) / Math.max(1, frameBottom - frameTop));
    const visibleW = width / scale;
    const visibleH = height / scale;
    let targetX = focus.x + PLAYER_W / 2 - visibleW * 0.43;
    // Default vertical framing: the focus (local) courier's SUPPORT LINE — the rest floor of its support
    // chain's root, so a rider frames the ground under its stack — sits ~72% down the frame: sky above,
    // ground and whatever lies below it (a brook, a bramble, a gap) in the lower band. Portrait keeps a
    // little more room above. An engaged canopy's floor still counts as the floor. The line is a REST
    // quantity: a jump does not move it, and a landing on another surface eases it over ~300 ms, so the
    // ground stays put while the body rises within the frame.
    const focusRoot = focusBody ? roots.get(focusBody.id) ?? focusBody : null;
    const focusRestY = focusBody ? this.memories.get(focusBody.id)?.restY ?? focusBody.y : focus.y;
    // The ground under the LAST support chain: a body that jumped off a friend's head (its own root while
    // airborne) still frames the ground its friend stands on until it lands somewhere else.
    const supportLine = Math.max(focusBody ? this.memories.get(focusBody.id)?.floorY ?? (focusRoot ?? focusBody).y + PLAYER_H : restFloor,
      ...engagedFields.map(hazard => hazard.y + hazard.h));
    const focusId = focusBody?.id ?? '';
    const ridingDeck = !!focusRoot?.supportId && (scene?.platforms.some(platform => platform.id === focusRoot.supportId && platform.motion) ?? false);
    const previousFloor = this.floor;
    const floorSnap = !this.ready || resized || reducedMotion || ridingDeck || !previousFloor || previousFloor.id !== focusId;
    const floorLine = !floorSnap && previousFloor ? previousFloor.line + (supportLine - previousFloor.line) * (1 - Math.exp(-dt * FLOOR_EASE_RATE)) : supportLine;
    // Same support as the frame already shows: the eased line has (all but) arrived at it.
    const sameSupport = !floorSnap && !!previousFloor && Math.abs(supportLine - previousFloor.target) <= SAME_SUPPORT && Math.abs(floorLine - supportLine) <= SAME_SUPPORT;
    this.floor = { id: focusId, line: Math.abs(floorLine - supportLine) <= 0.01 ? supportLine : floorLine, target: supportLine };
    const ceiling = CEILING_FRACTION * height / scale;
    let targetY = floorLine - height * (width < 500 ? FLOOR_LINE_PORTRAIT : FLOOR_LINE) / scale;
    // Jump room: an ordinary jump from rest of the whole support chain (the courier, its base, its riders —
    // a stack jumps together) stays inside the frame without the frame having to move.
    const chainRestTop = Math.min(focusRestY, ...alive.filter(player => linked.has(player.id)).map(player => this.memories.get(player.id)?.restY ?? player.y));
    targetY = Math.min(targetY, chainRestTop - JUMP_APEX - JUMP_HEADROOM);
    const padX = Math.min(18 / scale, Math.max(2 / scale, (visibleW - (right - left)) / 2));
    const padY = Math.min(16 / scale, Math.max(2 / scale, (visibleH - (frameBottom - frameTop)) / 2));
    const lowX = right + padX - visibleW, highX = left - padX;
    const lowY = frameBottom + padY - visibleH, highY = frameTop - padY;
    if (dangers.length) {
      // Soft bias toward the deadly surface and its near edge: the frame drops so the
      // water line sits inside it and leans outward over the bank edge. The scale is
      // never lowered for it, and the body constraints below always win.
      const dangerFloor = Math.max(...dangers.map(zone => zone.frame.y + zone.frame.h));
      const dangerLeft = Math.min(...dangers.map(zone => zone.frame.x));
      const dangerRight = Math.max(...dangers.map(zone => zone.frame.x + zone.frame.w));
      targetY = Math.max(targetY, dangerFloor + padY - visibleH);
      const dangerLowX = dangerRight + padX - visibleW, dangerHighX = dangerLeft - padX;
      if (dangerLowX <= dangerHighX) targetX = clamp(targetX, dangerLowX, dangerHighX);
    }
    if (keep) {
      // Soft bias toward a kept world rectangle (where a courier just fell): the frame leans so it
      // is inside, but only when it fits TOGETHER with the team at the current scale (a phone frame
      // that cannot hold both keeps the ordinary team framing). Never zooms for it; the team wins below.
      const fitsX = Math.max(right, keep.x + keep.w) - Math.min(left, keep.x) + padX * 2 <= visibleW;
      const fitsY = Math.max(frameBottom, keep.y + keep.h) - Math.min(frameTop, keep.y) + padY * 2 <= visibleH;
      if (fitsX) targetX = clamp(targetX, keep.x + keep.w + padX - visibleW, keep.x - padX);
      if (fitsX && fitsY) targetY = clamp(targetY, keep.y + keep.h + padY - visibleH, keep.y - padY);
    }
    targetX = clamp(targetX, lowX, highX);
    targetY = clamp(targetY, lowY, highY);
    targetX = clamp(targetX, 0, Math.max(0, WORLD_W - visibleW));
    targetY = clamp(targetY, 0, Math.max(0, WORLD_H - visibleH));
    if (local && level && scene) {
      // The world below the courier within the visible width: every uncovered water/bramble span that
      // overlaps the frame decided so far. The frame extends so the deadly surface (plus a little of the
      // hazard below it) is inside, and leans (by at most a tenth of the width) so the span AND its far
      // bank edge are inside when they fit at this scale. Both are weighted by how much of the span the
      // frame already shows (full weight from two body widths on), so a span entering at the edge eases
      // in rather than snapping. The scale is never lowered for it.
      const baseX = targetX;
      let lean = 0;
      const body = { x: local.x, y: local.y, w: PLAYER_W, h: PLAYER_H };
      for (const zone of dangerZones(level, scene.platforms, scene.world, body, visibleW)) {
        const [a, b] = zone.span;
        const inside = Math.min(b, baseX + visibleW) - Math.max(a, baseX);
        if (inside <= 0) continue;
        const strength = clamp(inside / Math.min(b - a, PLAYER_W * 2), 0, 1);
        const surfaceY = zone.surface + DANGER_SURFACE + padY - visibleH;
        if (surfaceY > targetY) targetY = mix(targetY, surfaceY, strength);
        const spanLow = b + FAR_BANK - visibleW, spanHigh = a - FAR_BANK;
        if (spanLow <= spanHigh) lean += (clamp(baseX, spanLow, spanHigh) - baseX) * strength;
      }
      targetX = baseX + clamp(lean, -visibleW * FAR_BANK_LEAN, visibleW * FAR_BANK_LEAN);
      // Never frames the RESTING courier above the top 15%: the extension yields before the courier does
      // (the airborne body is kept inside by the containment below, not by moving the frame with it).
      targetY = Math.min(targetY, focusRestY - ceiling);
      targetX = clamp(clamp(targetX, lowX, highX), 0, Math.max(0, WORLD_W - visibleW));
      targetY = clamp(clamp(targetY, lowY, highY), 0, Math.max(0, WORLD_H - visibleH));
    }
    // A courier at rest is framed where it stopped. The follow target leads a walking body by up to
    // ~20 world px (the easing lag), so easing on toward it after the stop used to slide the body
    // 5–16 px backwards on screen. While the focus body is at rest (zero velocity, grounded, its
    // support chain not riding a moving deck, the same body as last frame) the frame is HELD: the
    // current view, rescaled about the body so a slow zoom does not move it either, then clamped
    // to the containment and world limits, which alone may still move it (a teammate joining the
    // frame, a stack rising). New targets ease in only once the body moves again.
    const root = focusBody ? roots.get(focusBody.id) ?? null : null;
    const riding = !!root?.supportId && (scene?.platforms.some(platform => platform.id === root.supportId && platform.motion) ?? false);
    const still = !!focusBody && Math.abs(focusBody.vx) < 1 && Math.abs(focusBody.vy) < 1;
    // The hold starts on the very frame the velocity reaches zero (a body that was moving has just
    // stopped). A carried rider's velocity reads zero while it moves with its base, so a body that was
    // already still must also keep its place: walking covers ≥ 3.6 px per 60 Hz frame, the
    // prediction's own settle creeps < 2 px.
    const previous = this.rest;
    const atRest = still && focusBody.grounded && !riding && previous?.id === focusBody.id
      && (!previous.still || (Math.abs(previous.x - focusBody.x) < 2.4 && Math.abs(previous.y - focusBody.y) < 2.4));
    this.rest = focusBody ? { id: focusBody.id, x: focusBody.x, y: focusBody.y, still } : null;
    this.held = this.ready && !resized && atRest;
    if (this.held) {
      const k = this.view.scale / scale;
      const fx = focus.x + PLAYER_W / 2, fy = focus.y + PLAYER_H / 2;
      targetX = clamp(clamp(fx - (fx - this.view.x) * k, lowX, highX), 0, Math.max(0, WORLD_W - visibleW));
      // A body that just landed on another surface and stands still lets the floor line finish easing
      // to its new support (the vertical target above); only a settled support holds the height.
      if (sameSupport) targetY = clamp(clamp(fy - (fy - this.view.y) * k, lowY, highY), 0, Math.max(0, WORLD_H - visibleH));
    }
    // Airborne over the same support (a jump, a stack jump, a hop): the frame's vertical position is HELD —
    // rescaled about the floor line so a slow zoom does not move the ground either — and the body rises
    // within it. Only the containment and world limits may still move it (a very tall stack in a short
    // frame). A landing elsewhere changes the support line, which then eases the frame over ~300 ms.
    this.heldY = this.ready && !resized && !this.held && !!focusBody && !focusBody.grounded && !ridingDeck && sameSupport;
    if (this.heldY) {
      const k = this.view.scale / scale;
      targetY = clamp(clamp(floorLine - (floorLine - this.view.y) * k, lowY, highY), 0, Math.max(0, WORLD_H - visibleH));
    }
    const x = clamp(clamp(mix(this.view.x, targetX, smoothing), lowX, highX), 0, Math.max(0, WORLD_W - visibleW));
    // The eased frame follows the target; the team containment (every member inside, the local courier
    // included, airborne or not) still wins over it.
    const eased = this.heldY ? targetY : mix(this.view.y, targetY, smoothing);
    const y = clamp(clamp(eased, lowY, highY), 0, Math.max(0, WORLD_H - visibleH));
    this.view = { x, y, scale, offsetX: visibleW > WORLD_W ? (width - WORLD_W * scale) / 2 : 0,
      offsetY: visibleH > WORLD_H ? (height - WORLD_H * scale) / 2 : 0, width, height, follow, compact,
      teamIds: team.map(player => player.id), teamBounds: { x: left, y: top, w: right - left, h: bottom - top },
      dangers: dangers.map(zone => ({ ...zone, span: [...zone.span] as [number, number], frame: { ...zone.frame } })), held: this.held, heldY: this.heldY,
      floorLine: this.floor.line, supportLine };
    this.ready = true;
    return this.view;
  }
}
